import React, { useEffect, useMemo, useState } from "react";
import { useColors } from "../../../contexts/ColorContext";
import {
  Card,
  Spinner,
  Badge,
  Row,
  Col,
  Alert,
  Button,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import { API_BASE_URL as API_BASE } from "../../../config";

// UTIL – formata HH:mm
function toHourMinute(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}`;
  } catch {
    return "—";
  }
}

// UTIL — compara Y-M-D
function isSameYMD(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function Agenda({ usuario, idClienteMaster }) {
  const { colors } = useColors();
  const {
    primaryColor,
    secondaryColor,
    cardBackgroundColor,
    cardTextColor,
    buttonColor,
    buttonTextColor,
  } = colors;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

useEffect(() => {
  if (!idClienteMaster) return;

  const fetchAgendamentos = async () => {
    try {
      setLoading(true);
      setErr(null);

      const ano = selectedDate.getFullYear();
      const mes = selectedDate.getMonth();
      const dataInicio = new Date(ano, mes, 1).toLocaleDateString("en-CA");
      const dataFim = new Date(ano, mes + 1, 0).toLocaleDateString("en-CA");

      const agRes = await axios.get(`${API_BASE}/Agendamentos`, {
        params: { idClienteMaster, dataInicio, dataFim, tamanhoPagina: 200 },
      });

      setAgendamentos(Array.isArray(agRes.data.dados) ? agRes.data.dados : []);
    } catch (e) {
      console.error(e);
      setErr("Erro ao consultar agendamentos.");
    } finally {
      setLoading(false);
    }
  };

  fetchAgendamentos();
}, [idClienteMaster, selectedDate]);

const agendamentosDoDia = useMemo(() => {
  if (!usuario) return [];

  const filtroFuncionario = (a) =>
    (a.funcionario?.id ?? a.funcionarioId) === usuario.id;

  const filtroDia = (a) => {
    if (!a.dataHora) return false;
    return a.dataHora.substring(0, 10) === selectedDate.toLocaleDateString("en-CA");
  };

  return agendamentos
    .filter((a) => filtroFuncionario(a) && filtroDia(a))
    .sort((a, b) => new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime());
}, [agendamentos, usuario, selectedDate]);
  // AÇÕES
  const openConfirm = (ag) => {
    setToDelete(ag);
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (!deleting) {
      setShowConfirm(false);
      setToDelete(null);
    }
  };

  const handleMarcarRealizado = async (agendamento) => {
    const id = agendamento.id;

    try {
      await axios.put(`${API_BASE}/Agendamentos/realizado/${id}`);

      setAgendamentos((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, realizado: true } : a
        )
      );
    } catch (err) {
      console.error("Erro ao marcar como realizado:", err);
      window.globalAlert("Erro ao marcar agendamento como realizado.", "error");
    }
  };

  const confirmarExclusao = async () => {
    if (!toDelete) return;

    const id = toDelete.id;

    try {
      setDeleting(true);

      await axios.delete(`${API_BASE}/Agendamentos/${id}`);

      setAgendamentos((prev) => prev.filter((a) => a.id !== id));

      closeConfirm();
    } catch {
      window.globalAlert("Erro ao excluir agendamento.", "error");
      setDeleting(false);
    }
  };

  const isMobile = window.innerWidth <= 480;

const bubbleStyle = {
  backgroundColor: primaryColor,
  color: buttonTextColor,
  padding: isMobile ? "12px 6px" : "6px 12px", 
  borderRadius: "10px",
  fontWeight: 500,
  fontSize: isMobile ? "12px" : "14px",      
  alignItems: "center",        
  justifyContent: "center",     
  textAlign: "center"   
};
  // RENDER
  return (
    <div style={{ width: "100%" }}>
      {err && <Alert variant="danger">{String(err)}</Alert>}

      <Row
        className="g-4 justify-content-center align-items-start"
        style={{ margin: 0 }}
      >
        {/* CALENDÁRIO */}
        <Col
          xs={12}
          md={4}
          lg={4}
          className="d-flex justify-content-center align-items-start"
        >
          <Card
            className="shadow-sm border-0"
            style={{
              backgroundColor: cardBackgroundColor,
              color: cardTextColor,
              borderRadius: "12px",
              width: "100%",
              padding: "1rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              locale="pt-BR"
              prev2Label={null}
              next2Label={null}
              className="custom-calendar"
            />

            <style>{`
              .react-calendar {
                width: 100% !important;
                background-color: ${cardBackgroundColor} !important;
                color: ${cardTextColor} !important;
                border: none !important;
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                padding: 10px;
              }
              .react-calendar__navigation button {
                color: ${cardTextColor} !important;
                font-weight: 600;
                background: transparent !important;
                border: none !important;
              }
              .react-calendar__tile--active {
                background-color: ${primaryColor} !important;
                color: ${buttonTextColor} !important;
              }
              .react-calendar__tile--now {
                background-color: ${secondaryColor} !important;
                color: ${buttonTextColor} !important;
              }
            `}</style>
          </Card>
        </Col>

        {/* LISTA */}
        <Col xs={12} md={8} lg={8}>
          <Card
            className="shadow-sm border-0 mx-auto"
            style={{
              backgroundColor: cardBackgroundColor,
              color: cardTextColor,
              borderRadius: "12px",
              padding: "1rem",
              width: "100%",
              maxWidth: "850px",
            }}
          >
            <Card.Header
              className="fw-bold text-center"
              style={{
                backgroundColor: secondaryColor,
                color: buttonTextColor,
                borderRadius: "10px",
              }}
            >
              Agendamentos de {usuario?.nome} em{" "}
              {selectedDate.toLocaleDateString("pt-BR")}
            </Card.Header>

            <Card.Body
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1rem",
                padding: "2rem 1rem",
              }}
            >
              {loading && <Spinner animation="border" />}

              {!loading && agendamentosDoDia.length === 0 && (
                <div className="text-muted" style={{ fontStyle: "italic" }}>
                  Nenhum agendamento para este dia.
                </div>
              )}

              {agendamentosDoDia.map((ag, i) => {
                const realizado = ag.realizado;
                const servico = ag.servico?.nome ?? "—";
                const cliente = ag.cliente?.nome ?? "—";
                const telefone = ag.cliente?.telefone ?? "—";
                const hora = toHourMinute(ag.dataHora);

                const statusText = realizado ? "Realizado" : "Pendente";

                return (
                  <div
                    key={i}
                    className="p-4 w-100"
                    style={{
                      background: secondaryColor,
                      color: buttonTextColor,
                      borderRadius: "15px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                      border: `1px solid ${primaryColor}`,
                      maxWidth: "750px",
                    }}
                  >
                    <div className="d-flex justify-content-between mb-2">
                      <h5 className="mb-0">{servico}</h5>

 <span style={bubbleStyle}>
  {statusText}
</span>


                    </div>

                    <div>
                      <div><strong>Cliente:</strong> {cliente}</div>
                      <div><strong>Cel:</strong> {telefone}</div>
                      <div><strong>Hora:</strong> {hora}</div>
                      {ag.observacao && (
                        <div><strong>Obs:</strong> {ag.observacao}</div>
                      )}
                    </div>

                    <div className="d-flex justify-content-end mt-3">
                      {!realizado && (
                        <Button
                          size="sm"
                          onClick={() => handleMarcarRealizado(ag)}
                          style={{
                            backgroundColor: buttonColor,
                            borderColor: buttonColor,
                            color: buttonTextColor,
                            marginRight: "10px",
                          }}
                        >
                          Concluir
                        </Button>
                      )}

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openConfirm(ag)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* MODAL */}
      <Modal show={showConfirm} onHide={closeConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deseja realmente excluir este agendamento?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeConfirm}
            style={{
              backgroundColor: secondaryColor,
              borderColor: secondaryColor,
              color: buttonTextColor,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="danger"
            onClick={confirmarExclusao}
            disabled={deleting}
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              color: buttonTextColor,
            }}
          >
            {deleting ? "Excluindo..." : "Excluir"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
